import { ReportType } from "~/types/global";
import { SocialInputClassifier } from "./social-input.classifier";
import {
  validateCNPJ as validateCNPJDocument,
  isValidAlphanumericCNPJ,
  validatePlaca as validatePlacaDocument,
  validateChassi as validateChassiDocument,
} from "./document-validators";

/**
 * Resultado da validação de entrada
 */
export type ValidationResult =
  | { isValid: true; type: ReportType }
  | { isValid: false; type: null };

/**
 * Serviço responsável por validações de entrada de relatórios
 */
export class ValidationService {
  /**
   * Valida entrada completa de relatório
   * Verifica se o input é válido e retorna o tipo detectado
   */
  static validateReportInput(input: string): ValidationResult {
    if (typeof input !== 'string') {
      return { isValid: false, type: null };
    }

    const trimmedInput = input.trim();
    if (!trimmedInput) return { isValid: false, type: null };

    // Placa (formato antigo AAA9999 ou Mercosul AAA9A99) ou chassi/VIN (17
    // alfanuméricos) têm prioridade sobre a classificação social genérica:
    // sem essa checagem, ambos batem no Handle_Format (3-30 alfanuméricos) e
    // são confundidos com um handle de rede social — mesmo racional de
    // CPF/CNPJ vencerem o fallback de ID numérico.
    if (this.validatePlaca(trimmedInput) || this.validateChassi(trimmedInput)) {
      return { isValid: true, type: 'veiculo' };
    }

    // CNPJ alfanumérico (novo padrão: 12 posições [A-Z0-9] + 2 dígitos de DV):
    // contém letras, então bateria no Handle_Format do classificador social
    // abaixo e seria confundido com um handle de rede social. Precisa vencer
    // por especificidade (mesmo racional de placa/chassi acima e do CNPJ
    // numérico vencer o fallback de ID numérico). O formato numérico antigo é
    // tratado no bloco numérico mais abaixo.
    if (isValidAlphanumericCNPJ(trimmedInput)) {
      return { isValid: true, type: 'cnpj' };
    }

    const social = SocialInputClassifier.classify(trimmedInput);
    if (social.matched) return { isValid: true, type: 'redes_sociais' };
    if (social.reason === 'insufficient-length') return { isValid: false, type: null };

    // URLs não são entradas válidas para criar relatórios
    if (this.isUrl(trimmedInput)) return { isValid: false, type: null };

    if (trimmedInput.includes('@')) {
      return this.isEmailValid(trimmedInput)
        ? { isValid: true, type: 'email' }
        : { isValid: false, type: null };
    }

    const cleanInput = trimmedInput.replace(/\D/g, "");
    const len = cleanInput.length;

    if (len === 11 && this.validateCPF(cleanInput)) {
      return { isValid: true, type: 'cpf' };
    }

    if (len === 14 && this.validateCNPJ(cleanInput)) {
      return { isValid: true, type: 'cnpj' };
    }

    if (len >= 10 && len <= 13 && this.isPhoneValid(cleanInput)) {
      return { isValid: true, type: 'telefone' };
    }

    // Numérico inválido como documento, em comprimento terminal → possível ID de rede social.
    if (this.isPossibleSocialNumericId(cleanInput)) {
      return { isValid: true, type: 'redes_sociais' };
    }

    return { isValid: false, type: null };
  }

  /**
   * Numérico que não valida como documento, em comprimento NÃO-documento
   * (≥15) e não trivial (dígitos repetidos): possível ID de rede social.
   *
   * ALINHADO AO BACKEND (US 21941, autoridade de detecção em
   * `report_type_detection.py`): 11 dígitos inválidos são `cpf` (inválido) e
   * 14 são `cnpj` (inválido) — NUNCA `redes_sociais`. Um CPF em formação/
   * inválido não pode ser sequestrado como ID social só porque o DV não fecha;
   * o backend devolve `cpf` para esse caso, então o detector local tem de
   * concordar. Comprimentos de documento (10–14) permanecem documento; só
   * ≥15 dígitos (fora de qualquer faixa de documento BR) viram ID social.
   */
  static isPossibleSocialNumericId(digits: string): boolean {
    if (!/^\d+$/.test(digits) || this.hasAllRepeatedDigits(digits)) return false;
    return digits.length >= 15;
  }

  /**
   * Valida formato de placa de veículo brasileira: AAA9999 (antigo) ou
   * AAA9A99 (Mercosul). Aceita espaço/hífen entre as letras e os dígitos
   * (ex.: "ABC-1234"). Formato apenas — a Snap é quem valida a placa de
   * fato (mesmo racional do backend: nenhum tipo simples valida documento
   * localmente, ver `apps/backend/domains/reports/services/report.py`).
   */
  static validatePlaca(placa: string): boolean {
    return validatePlacaDocument(placa);
  }

  /**
   * Valida formato de chassi/VIN de veículo: 17 caracteres alfanuméricos,
   * sem I/O/Q (padrão VIN — evitam confusão visual com 1/0), com ao menos
   * uma letra — nenhum VIN real é inteiramente numérico, e sem essa
   * exigência uma string qualquer de 17 dígitos (ex.: um ID interno) batia
   * como chassi válido. A Snap aceita chassi como alternativa a placa no
   * mesmo endpoint (confirmado no OpenAPI da Snap, 2026-09-03) — formato
   * apenas, mesmo racional de `validatePlaca`.
   */
  static validateChassi(chassi: string): boolean {
    return validateChassiDocument(chassi);
  }

  /**
   * Verifica se a string é uma URL válida
   */
  static isUrl(input: string): boolean {
    if (typeof input !== 'string' || !input) return false;

    // Verifica protocolo explícito (http://, https://, ftp://, etc)
    if (/^(https?|ftp|ftps|mailto|file):\/\//i.test(input)) return true;

    // Verifica se começa com www.
    if (/^www\./i.test(input)) return true;

    return false;
  }

  /**
   * Valida formato de email
   */
  static isEmailValid(email: string): boolean {
    // Regex para validação básica de email + limite de tamanho
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email) && email.length <= 254;
  }

  /**
   * Valida telefone brasileiro (fixo ou celular, com ou sem DDD)
   */
  static isPhoneValid(numero: string): boolean {
    // Telefone com +55 no início
    if (numero.startsWith('55')) {
      numero = numero.substring(2);
    }

    // Telefone fixo com DDD: DDD (11-99) + 8 dígitos começando com 2-9
    if (numero.length === 10) {
      const ddd = numero.substring(0, 2);
      const num = numero.substring(2);

      // Verificar se os dígitos após o DDD são todos iguais
      if (/^(\d)\1{7}$/.test(num)) {
        return false;
      }

      return parseInt(ddd) >= 11 && parseInt(ddd) <= 99 && /^[2-9]\d{7}$/.test(num);
    }

    // Telefone celular com DDD: DDD (11-99) + 9 dígitos começando com 9
    if (numero.length === 11) {
      const ddd = numero.substring(0, 2);
      const num = numero.substring(2);

      // Verificar se os dígitos após o DDD são todos iguais
      if (/^(\d)\1{8}$/.test(num)) {
        return false;
      }

      return parseInt(ddd) >= 11 && parseInt(ddd) <= 99 && /^9\d{8}$/.test(num);
    }

    // Telefone fixo sem DDD: 8 dígitos começando com 2-9
    if (numero.length === 8) {
      return /^[2-9]\d{7}$/.test(numero);
    }

    // Telefone celular sem DDD: 9 dígitos começando com 9
    if (numero.length === 9) {
      return /^9\d{8}$/.test(numero);
    }

    return false;
  }

  /**
   * Verifica se o número tem todos os dígitos repetidos
   */
  static hasAllRepeatedDigits(digits: string): boolean {
    if (digits.length === 0) return false;
    const firstDigit = digits[0];
    return digits.split('').every(d => d === firstDigit);
  }

  /**
   * Valida se o número de telefone é válido (não começa com 0 após DDD)
   */
  static isValidPhoneNumber(digits: string): boolean {
    if (digits.length < 3) return true;

    if (this.hasAllRepeatedDigits(digits)) return false;

    const thirdDigit = digits[2];
    if (thirdDigit === '0') return false;

    const phoneDigits = digits.substring(2);
    if (phoneDigits.length > 0 && this.hasAllRepeatedDigits(phoneDigits)) return false;

    return true;
  }

  /**
   * Valida CPF usando dígitos verificadores
   */
  static validateCPF(cpf: string): boolean {
    const digits = cpf.replace(/\D/g, '');

    // Verifica se tem 11 dígitos e não é sequência repetida
    if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) {
      return false;
    }

    // Calcula primeiro dígito verificador
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(digits[i]) * (10 - i);
    }
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;

    if (resto !== parseInt(digits[9])) {
      return false;
    }

    // Calcula segundo dígito verificador
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(digits[i]) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;

    return resto === parseInt(digits[10]);
  }

  /**
   * Valida CNPJ usando dígitos verificadores. Suporta os DOIS padrões: o
   * antigo (14 dígitos) e o novo alfanumérico (12 posições [A-Z0-9] + 2 DV
   * numéricos). Aceita com ou sem máscara. Delega ao validador centralizado.
   */
  static validateCNPJ(cnpj: string): boolean {
    return validateCNPJDocument(cnpj);
  }
}
